import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import reportPackService from '@api/v1/reportPackService'
import useReadState from '@hooks/useReadState'
import { fmtWhenInZone } from '@components/reports/queue/queueFormat'
import { memberOutcomeText, runDeliveryText, runIncludedText } from './packFormat'

/**
 * A pack's run history (spec 172): each run, what each report in it did, and what happened
 * to the email. A finished report links to its own saved result. A failed read says so — it
 * never renders as "no runs yet".
 */
const PackRunHistory = ({ pack }) => {
  const query = useQuery({
    queryKey: ['report-pack-runs', pack.id],
    queryFn: () => reportPackService.runs(pack.id),
    refetchInterval: (q) => ((q.state.data || []).some((r) => r.state !== 'done') ? 10_000 : false),
  })
  const state = useReadState(`report-pack-runs:${pack.id}`, query)
  const runs = state === 'ready' ? query.data : []

  if (state === 'loading' || state === 'idle') return <p className="text-[12px] text-gray-600">Loading runs…</p>
  if (state !== 'ready') {
    return (
      <p role="alert" className="text-[12px] text-red-800">
        This pack&apos;s runs couldn&apos;t be loaded{query.error?.message ? `: ${query.error.message}` : ''}. This is not an empty history.{' '}
        <button type="button" onClick={() => query.refetch()} className="font-semibold text-cb-accent-text underline">Try again</button>
      </p>
    )
  }
  if (!runs.length) return <p className="text-[12px] text-gray-600" data-testid="pack-runs-empty">This pack hasn&apos;t run yet.</p>

  return (
    <ol className="space-y-3" data-testid="pack-run-history">
      {runs.map((run) => (
        <li key={run.id} className="rounded-lg border border-gray-200 bg-white px-3 py-2">
          <div className="flex flex-wrap items-baseline gap-x-3 text-[12.5px]">
            <span className="font-semibold text-gray-900">{fmtWhenInZone(run.createdAt, pack.timezone)}</span>
            <span className="text-gray-600">{run.trigger === 'manual' ? 'Run now' : 'On schedule'}</span>
            <span className="text-gray-700">{runIncludedText(run)}</span>
            <span className="text-gray-700" data-testid="pack-run-delivery">{runDeliveryText(run)}</span>
          </div>
          {run.delivery?.note && run.delivery.status !== 'skipped' && (
            <p className="mt-1 text-[11.5px] text-gray-700">{run.delivery.note}</p>
          )}
          <ul className="mt-1.5 space-y-0.5">
            {run.members.map((m) => (
              <li key={m.position} className="text-[12px] text-gray-800">
                <span className="font-medium">{m.label}</span>{' — '}
                <span className={m.outcome && m.outcome !== 'ready' ? 'text-red-800' : 'text-gray-700'}>{memberOutcomeText(m)}</span>
                {m.outcome === 'ready' && m.jobId && (
                  <>
                    {' · '}
                    <Link to={`/reports/${encodeURIComponent(m.reportId)}?jobId=${encodeURIComponent(m.jobId)}`} className="text-cb-accent-text underline" aria-label={`Open ${m.label}`}>
                      Open
                    </Link>
                  </>
                )}
              </li>
            ))}
          </ul>
        </li>
      ))}
    </ol>
  )
}

export default PackRunHistory
